import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import classNames from "classnames";
import { useSelector, useDispatch } from "react-redux";
import { dequal } from "dequal";

import Tooltip from "@/components/common/Tooltip/Tooltip";
import SideNavSubRoute from "@components/layout/SideNav/SideNavSubRoute";

import type { ChildRoute, RouteConfig } from "@routes/routes.types";
import type { RootState } from "@store/store";

import HarrisLogo from "@assets/icons/projectSpecific/HQB_logo.svg?react";
import AngleArrow from "@assets/icons/common/angle_down.svg?react";
import { FiSettings } from "react-icons/fi";
import ICONS from "@/constant/IconManager";
import CONFIG from "@/config";
import { useRouteLabel } from "@/hooks/selectors/useRouteLabel";
import {
  URL_BY_BRAND,
  URL_BY_MARKET,
  URL_BY_MODEL,
  URL_BY_SEGMENT,
} from "@/constant/constantsThrougoutProject";
import Tippy from "@tippyjs/react";
import userService from "@/endpoints/userService";
import SignoutIcon from "@assets/icons/common/signout.svg?react";
import "./SideNav.css";
import { setPendingSelection } from "@/store/slices/pendingSelection/pendingSelectionSlice";
import { SIDE_NAV_UI_ROLES } from "@/constants/uiRoles";
import { slugify } from "@/utils/stringUtility";

interface SideNavProps {
  routes: RouteConfig[];
}

const NAV_CHILD_PRIORITY = [
  URL_BY_BRAND,
  URL_BY_SEGMENT,
  URL_BY_MARKET,
  URL_BY_MODEL,
];

function resolveTargetChild(
  route: RouteConfig,
  pathname: string,
): ChildRoute | undefined {
  if (!route.children || route.children.length === 0) {
    return undefined;
  }

  const { children } = route;
  let targetChild: ChildRoute | undefined;

  const currentPathSegment = NAV_CHILD_PRIORITY.find((p) =>
    pathname.includes(`/${p}`),
  );

  if (currentPathSegment) {
    targetChild = children.find((child) =>
      child.id.toString().includes(currentPathSegment),
    );
  }

  if (!targetChild) {
    for (const p of NAV_CHILD_PRIORITY) {
      targetChild = children.find((child) => child.id.toString().includes(p));
      if (targetChild) break;
    }
  }

  return targetChild ?? children[0];
}

function isModifiedClick(event: React.MouseEvent) {
  return (
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    event.button !== 0
  );
}

const SideNav = ({ routes }: SideNavProps) => {
  const [isAlwaysExpanded, setIsAlwaysExpanded] = useState(false);
  const location = useLocation();
  const dispatch = useDispatch();
  const [activeRoute, setActiveRoute] = useState<RouteConfig | null>(null);
  const [hoveredRoute, setHoveredRoute] = useState<RouteConfig | null>(null);
  const handleRedirect = (route: RouteConfig) => {
    const targetChild = resolveTargetChild(route, location.pathname);
    if (!targetChild) {
      return;
    }

    const newPathname = targetChild.redirectTo;
    const parts = newPathname.split("/").filter(Boolean);
    const module = parts[0] || "";
    const screenType = parts[1] || "brand";
    const pending: Record<string, any> = { module, screenType };

    //To display the Familiar audience type as default for reputation
    if (route.id === "reputation") {
      pending.base = "FAMILIAR";
    }

    dispatch(setPendingSelection(pending as any));
  };

  useLayoutEffect(setActiveRouteFromPath, [location.pathname]);

  function setActiveRouteFromPath() {
    const result = routes.find((route) =>
      location.pathname?.startsWith(`/${route.id}`),
    );
    if (result) {
      setActiveRoute(result);
    } else {
      setActiveRoute(null);
    }
  }

  const isAdmin = useSelector(
    (state: RootState) => state.userDetail?.isAdmin,
    (prev, next) => dequal(prev, next),
  );

  const user = useSelector(
    (state: RootState) => state.userDetail || null,
    (prev, next) => {
      return dequal(prev, next);
    },
  );

  const resolveRouteLabel = useRouteLabel();

  return (
    <div
      data-ui-id={SIDE_NAV_UI_ROLES.CONTAINER}
      className={classNames("flex h-screen shadow-xs text-text-secondary")}
      onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
        const relatedTarget = e.relatedTarget;
        if (
          relatedTarget instanceof Element &&
          (relatedTarget.closest('[data-id="sidenav-submenu"]') ||
            relatedTarget.closest(".tippy-box") ||
            relatedTarget.closest("[data-tippy-root]"))
        ) {
          return;
        }
        setHoveredRoute(null);
        setActiveRouteFromPath();
      }}
    >
      <div className="relative h-screen w-[71px] bg-secondary-500">
        <div className="flex justify-center align-center px-2 pt-[6px] pb-[10px] m-1 mt-3 my-2">
          <HarrisLogo height={32} width={32} />
        </div>
        <div className="flex flex-col items-center justify-between h-[calc(100%-67px)]">
          <ul className="flex flex-col items-center pt-3 justify-center gap-[10px]">
            {routes
              ?.filter((route) => !route.hideNavIcon)
              .map((route) => {
                const targetChild = resolveTargetChild(route, location.pathname);
                const href = targetChild
                  ? `${targetChild.redirectTo}${location.search ?? ""}`
                  : undefined;

                return (
                  <Tooltip
                    key={route.id}
                    contentList={[resolveRouteLabel(route)]}
                    offset={isAlwaysExpanded ? [0, 5] : [0, 20]}
                    placement="right"
                  >
                    <li
                      data-ui-id={`${SIDE_NAV_UI_ROLES.ITEM}-${slugify(route.id)}`}
                      className={classNames(
                        "rounded-lg border-1 cursor-pointer",
                        !href && "p-[10px]",
                        location.pathname?.startsWith(`/${route.id}`)
                          ? "bg-ternary-500"
                          : "",
                        !location.pathname?.startsWith(`/${route.id}`) &&
                          hoveredRoute &&
                          hoveredRoute.id === route.id
                          ? "border-[#1b404d]"
                          : "border-transparent",
                      )}
                      onMouseEnter={() => {
                        setHoveredRoute(route);
                      }}
                      onClick={(event) => {
                        if (isModifiedClick(event)) return;
                        handleRedirect(route);
                      }}
                    >
                      {href ? (
                        <Link
                          to={href}
                          aria-label={route.name}
                          className="block p-[10px] text-inherit no-underline outline-none"
                          onClick={(event) => {
                            if (isModifiedClick(event)) return;
                            event.preventDefault();
                          }}
                        >
                          {route.iconKey ? (
                            <img
                              src={ICONS[route.iconKey]?.light}
                              alt={route.name}
                              className="h-[22px] w-[22px]"
                            />
                          ) : null}
                        </Link>
                      ) : route.iconKey ? (
                        <img
                          src={ICONS[route.iconKey]?.light}
                          alt={route.name}
                          className="h-[22px] w-[22px]"
                        />
                      ) : null}
                    </li>
                  </Tooltip>
                );
              })}
          </ul>
          <div className="flex flex-col items-center">
            {isAdmin && (
              <Tooltip
                contentList={["Admin"]}
                offset={[0, 5]}
                placement="right"
              >
                <a
                  data-ui-id={SIDE_NAV_UI_ROLES.ADMIN}
                  href={CONFIG.endPoints.legacyUi}
                  aria-label="Admin"
                  className="block p-[10px] rounded-lg border-1 border-transparent cursor-pointer hover:bg-ternary-500 transition-colors mb-[10px] text-inherit no-underline outline-none"
                >
                  <FiSettings className="h-[20px] w-[20px] text-white" />
                </a>
              </Tooltip>
            )}
            <div className="border-t-2 border-[#1b404d54] px-1 py-[19px]">
              {isAlwaysExpanded ? (
                <Tooltip
                  contentList={user?.name ? [user?.name] : ["Loading..."]}
                  offset={[0, 5]}
                >
                  <div
                    className={classNames(
                      "w-[40px] h-[40px] rounded-[50%] flex items-center justify-center",
                      user?.name
                        ? "bg-[#F2F4F7] text-[#667085] font-semibold text-large"
                        : "bg-[#F2F4F7] opacity-50",
                    )}
                  >
                    {user?.name ? user?.name.slice(0, 1)?.toUpperCase() : ""}
                  </div>
                </Tooltip>
              ) : (
                <Tippy
                  interactive={true}
                  trigger="click"
                  zIndex={900}
                  className={`user-name-tooltip-container`}
                  arrow={false}
                  offset={[5, 18]}
                  placement={"right-end"}
                  content={[
                    <div>
                      <div className="w-full">
                        <div className="border-t-[2px] pr-0 py-[6px] pl-0 border-[#1b404d54] mx-2 text-base flex items-center justify-between">
                          <div className="w-[calc(100%-22px)] h-[40px]">
                            {user?.name ? (
                              <div className="font-medium">{user?.name}</div>
                            ) : (
                              ""
                            )}
                            {user?.email && (
                              <Tooltip
                                contentList={[
                                  user?.email
                                    ? user?.email
                                    : "Something went wrong",
                                ]}
                                offset={[0, 5]}
                                placement="top"
                              >
                                <div className="text-xs font-light text-ellipsis overflow-hidden opacity-70 w-full">
                                  {user?.email}
                                </div>
                              </Tooltip>
                            )}
                          </div>
                          <Tooltip
                            contentList={["Sign out"]}
                            offset={[0, 5]}
                            placement="right"
                          >
                            <div
                              className="p-1 cursor-pointer"
                              onClick={userService.auth.logout}
                            >
                              <SignoutIcon height={22} />
                            </div>
                          </Tooltip>
                        </div>
                      </div>
                    </div>,
                  ]}
                >
                  <div
                    className={classNames(
                      "w-[40px] h-[40px] rounded-[50%] cursor-pointer flex items-center justify-center",
                      user?.name
                        ? "bg-[#F2F4F7] text-[#667085] font-semibold text-large"
                        : "bg-[#F2F4F7] opacity-50",
                    )}
                  >
                    {user?.name ? user?.name.slice(0, 1)?.toUpperCase() : ""}
                  </div>
                </Tippy>
              )}
            </div>
          </div>
        </div>
        <Tooltip
          contentList={[isAlwaysExpanded ? "Unpin" : "Pin"]}
          offset={[5, 5]}
          placement="top"
        >
          <div
            data-ui-id={SIDE_NAV_UI_ROLES.PIN}
            onClick={() => {
              setIsAlwaysExpanded((prev) => !prev);
            }}
            className="rounded-[50%] absolute z-[101] bottom-[92px] right-[-12px] bg-secondary-500 border w-fit leading-none cursor-pointer p-1  border-[#1b404d]"
          >
            <AngleArrow
              height={16}
              width={16}
              className={classNames(
                "transition",
                isAlwaysExpanded ? "rotate-[90deg]" : "rotate-[-90deg]",
              )}
            />
          </div>
        </Tooltip>
      </div>

      {isAlwaysExpanded && (
        <div
          data-id="sidenav-submenu"
          data-ui-id={SIDE_NAV_UI_ROLES.SUBMENU}
          className={classNames(
            "bg-secondary-500 transition relative w-[221px] border-l  border-[#1b404d] h-full",
          )}
        >
          <SideNavSubRoute
            activeRoute={hoveredRoute || activeRoute}
            setIsExpanded={() => {}}
            isExpanded={isAlwaysExpanded}
          />
        </div>
      )}
    </div>
  );
};

export default SideNav;
